<template>
  <div class="flex flex-col md:flex-row mb-12 course-form">
    <div class="w-full md:w-3/5 content-left-panel">
      <div class="flex flex-wrap justify-end name-field">
        <vs-input
          class="w-full"
          label="Course name"
          v-model="localCourse.name"
        />
      </div>
      <div class="flex flex-wrap justify-between mt-10">
        <div class="w-full">
          <label class="vs-input--label">Slug</label>
          <vx-input-group
            class="mb-base"
            prependClasses="border border-solid border-grey border-r-0"
          >
            <template slot="prepend">
              <div class="prepend-text">
                <vs-switch
                  v-model="autoAlias"
                  :checked="autoAlias"
                  class="auto-alias-checkbox"
                />
              </div>
            </template>

            <vs-input
              class="auto-alias-field"
              v-model="localCourse.slug"
              :disabled="autoAlias"
            />
          </vx-input-group>
        </div>
      </div>
      <div class="w-full flex flex-wrap">
        <div class="flex w-full md:w-1/2 px-10 mb-5 md:mt-5 md:mb-10">
          <vs-switch
            v-model="localCourse.is_featured"
            :checked="localCourse.is_featured"
            class="mr-5 is-featured-field"
          />
          <label>Is featured</label>
        </div>
        <div class="flex w-full md:w-1/2 px-10 mb-5 md:mt-5 md:mb-10">
          <vs-switch
            v-model="localCourse.has_cc"
            :checked="localCourse.has_cc"
            class="mr-5 has-cc-field"
          />
          <label>Has closed captioning</label>
        </div>
        <div class="flex w-full md:w-1/2 px-10 mb-5">
          <vs-switch
            v-model="localCourse.under_construction"
            :checked="localCourse.under_construction"
            class="mr-5 under-construction-field"
          />
          <label>Under construction</label>
        </div>
        <div
          class="w-full md:w-1/2 px-10 mb-5"
          v-if="customContentCoursesEnabled"
        >
          <vx-tooltip
            v-if="customContentToggleTooltip"
            :text="customContentToggleTooltip"
            class="custom-content-toggle-tooltip"
          >
            <div class="flex items-center">
              <vs-switch
                v-model="isCustomContentEnabled"
                :checked="isCustomContentEnabled"
                class="mr-5 is-custom-content-field"
                :disabled="isCustomContentToggleDisabled"
              />
              <label>{{ customContentStrings.toggleLabel }}</label>
            </div>
          </vx-tooltip>
          <template v-else>
            <div class="flex items-center">
              <vs-switch
                v-model="isCustomContentEnabled"
                :checked="isCustomContentEnabled"
                class="mr-5 is-custom-content-field"
                :disabled="isCustomContentToggleDisabled"
              />
              <label>{{ customContentStrings.toggleLabel }}</label>
            </div>
          </template>
          <p class="text-sm text-grey mt-2 whitespace-pre-line">
            {{ customContentStrings.publicStateHelperText }}
          </p>
        </div>
        <transition name="enterprise-accounts-dd">
          <div
            class="w-full px-10 pb-5"
            v-if="customContentCoursesEnabled && isCustomContentEnabled"
          >
            <label class="vs-input--label">{{
              customContentStrings.enterpriseAccountsLabel
            }}</label>
            <v-select
              ref="enterpriseAccountsSelect"
              class="enterprise-accounts-field"
              multiple
              v-model="localCourse.business_account_ids"
              :closeOnSelect="false"
              :loading="enterpriseAccountsLoading"
              :dropdownShouldOpen="enterpriseAccountsDropdownShouldOpen"
              :autoscroll="false"
              :reduce="reduceEnterpriseAccountOption"
              :getOptionLabel="getEnterpriseAccountOptionLabel"
              :placeholder="customContentStrings.enterpriseAccountsPlaceholder"
              :options="enterpriseAccountDropdownOptions"
              :filterable="false"
              @search="onSearchEnterpriseAccounts"
              @open="onOpenEnterpriseAccounts"
              @close="onCloseEnterpriseAccounts"
            >
              <template slot="no-options">
                <span v-if="showEnterpriseAccountsNoResults">
                  {{ customContentStrings.enterpriseAccountsNoMatches }}
                </span>
                <span v-else>
                  {{ enterpriseAccountsNoOptionsText }}
                </span>
              </template>
            </v-select>
            <span
              v-if="enterpriseAccountsValidationError"
              class="text-danger text-sm mt-1 block"
            >
              {{ enterpriseAccountsValidationError }}
            </span>
          </div>
        </transition>
      </div>
      <Skills :content-type="CONTENT_TYPES.COURSE" :skills-list="localCourse.taxonomy_skills" :editMode="false" />
      <div class="w-full">
        <vs-textarea
          class="mb-0 md:mt-10 mt-6 bg-white"
          rows="4"
          label="Description"
          v-model="localCourse.description"
        />
      </div>
      <div
        id="course-async-api-data-2"
        class="flex mt-10 flex-wrap vs-con-loading__container"
      >
        <div class="w-full pb-5">
          <label class="vs-input--label">Categories</label>
          <v-select
            multiple
            v-model="localCourse.categories"
            placeholder="Select one or more categories"
            :reduce="(category) => category.value"
            :options="getCategories"
          />
        </div>
        <div class="w-1/2 pb-5 pr-3">
          <label class="vs-input--label">Difficulty</label>
          <v-select
            v-model="localCourse.difficulty"
            placeholder="Select a level"
            :reduce="(difficulty) => difficulty.value"
            :options="getCourseDifficulties"
          />
        </div>
        <div class="w-1/2 pb-5 pl-3">
          <label class="vs-input--label">Learning Areas</label>
          <v-select
            multiple
            v-model="localCourse.learning_areas"
            placeholder="Select one or more areas"
            :options="getLAreas"
            :reduce="(la) => la.value"
          />
        </div>
        <div class="w-1/2 pb-5 pr-3">
          <label class="vs-input--label">Main Learning Area</label>
          <v-select
            v-model="localCourse.main_learning_area"
            placeholder="Select main area"
            :options="mainLAreaOptions"
            :reduce="(la) => la.value"
            :resetOnOptionsChange="validateMainLArea"
          />
        </div>
        <div class="w-1/2 pb-5 pl-3">
          <label class="vs-input--label">Vendor</label>
          <v-select
            v-model="localCourse.vendor"
            placeholder="Select a vendor"
            :reduce="(vendor) => vendor.value"
            :options="getVendors"
          />
        </div>
        <div class="w-full pb-5">
          <label class="vs-input--label">Tags</label>
          <v-select
            v-model="localCourse.tags"
            multiple
            taggable
            push-tags
            :closeOnSelect="false"
            placeholder="Select one or more tags"
            :options="tagsOptions"
            :filterable="false"
            @search="onSearchTags"
          >
            <template slot="no-options">type to search or add a tag</template>
          </v-select>
        </div>
        <div class="w-full pb-5">
          <label class="vs-input--label">Instructors</label>
          <v-select
            multiple
            v-model="localCourse.instructors"
            :closeOnSelect="false"
            :reduce="(instructor) => instructor.value"
            placeholder="Select one or more instructors"
            :options="getInstructors"
          />
        </div>
        <div class="flex w-full flex-wrap pb-10">
          <label class="vs-input--label">Bootcamp related</label>
          <v-select
            class="w-full"
            v-model="localCourse.bootcamp_origin"
            placeholder="Select a bootcamp"
            :options="bootcampOptions"
            :filterable="false"
            @search="onLocalSearchBootcamps"
            :reduce="(bootcamp) => bootcamp.value"
          >
            <template slot="no-options">type to search a bootcamp</template>
          </v-select>
        </div>
      </div>
      <vs-alert
        color="danger"
        icon-pack="feather"
        icon="icon-info"
        class="mb-5"
        v-if="
          localCourse.content && !localCourse.content.length && rememberToSave
        "
      >
        Please save the course before upload another CSV
      </vs-alert>
      <div class="flex flex-wrap mt-10 structure-import" v-if="canUploadCSV">
        <label class="w-full vs-input--label">Course structure</label>
        <upload-file
          v-if="!rememberToSave"
          automatic
          v-model="localCourse.structureFiles"
          class="w-full pl-0"
          text="Upload the course structure"
          @on-success="successUpload"
          @on-error="errorUpload"
          :uploadMethod="uploadCourseCSVWrapper"
        />
        <div
          class="flex cursor-pointer w-1/4 mb-5 add mt-5 add-grouper"
          @click.stop="
            () => {
              addGroup({ parent: null, index: 0, type: 'group' })
            }
          "
        >
          <feather-icon
            icon="PlusCircleIcon"
            svgClasses="h-5 w-5 color-current text-grey"
            class="flex items-center ml-1 mr-2"
          />
          Add a Group
        </div>
      </div>
      <div
        class="mt-5 tree-structure"
        v-if="localCourse.content && localCourse.content.length"
      >
        <div class="my-10" v-if="importCsvErrors && importCsvErrors.length">
          <ul class="import-csv-errors pl-10">
            <li v-for="([error, video], index) in importCsvErrors" :key="index">
              <span class="text-danger">{{ error }}</span>
              in {{ video }}
            </li>
          </ul>
        </div>
        <vs-alert
          color="warning"
          icon-pack="feather"
          icon="icon-info"
          class="mb-5"
          v-if="rememberToSave"
        >
          Please remember to save the course after edit the structure.
        </vs-alert>
        <course-structure
          :content="localCourse.content"
          :namespace="'courses'"
          :duration="localCourse.duration_in_seconds"
          :videos="getVideoEntries"
          :quizzes="getQuizEntries"
          :exercises="getExerciseEntries"
          :labs="getLabEntries"
          :iframes="getIFrameEntries"
          :canEditLeafs="true"
          :searchingRelatedOf="searchingRelatedOf"
          @delete-structure="deleteCourseStructure"
          @open-content="openToEdit"
          @add-group="addGroup"
          @add-leaf="addLeaf"
          @delete-group="deleteGroup"
          @delete-leaf="deleteLeaf"
          @rename-group="renameGroup"
        />
      </div>
    </div>
    <div
      class="w-full md:w-2/5 pl-0 md:pl-10 content-right-panel flex flex-col"
      sticky-container
    >
      <div class="flex flex-wrap mb-10" v-if="isEditing">
        <div class="w-full content-status">
          <label class="vs-input--label">Status</label>
          <v-select
            v-model="localCourse.status"
            :clearable="false"
            placeholder="Select one"
            :options="getCoursePossibleStatus"
            :reduce="(status) => status.value"
          />
        </div>
        <span v-if="isEditing" class="mt-2 text-grey my-ine-url"
          >View this <i>in my.ine</i>:
          <a
            target="_blank"
            :href="`${myIneUrl}/course/${localCourse.slug}/${localCourse.id}`"
            >{{ localCourse.name }}</a
          ></span
        >
      </div>
      <div class="flex flex-wrap mb-10" v-if="isEditing">
        <div
          class="w-full mb-5 published-date"
          v-if="localCourse.published_date"
        >
          <label class="vs-input--label">Published on</label>
          <vs-input
            class="w-full"
            disabled
            :value="localCourse.published_date | dateFormat"
          ></vs-input>
        </div>
        <div class="w-1/2 modified-date">
          <label class="vs-input--label">Modified on</label>
          <vs-input
            disabled
            :value="localCourse.modified | dateFormat"
          ></vs-input>
        </div>
        <div class="w-1/2 pl-1 created-date">
          <label class="vs-input--label">Created on</label>
          <vs-input
            disabled
            :value="localCourse.created | dateFormat"
          ></vs-input>
        </div>
      </div>
      <div
        class="flex action-buttons order-last md:order-normal mt-5 md:mt-0"
        v-sticky
        sticky-offset="{top: 25}"
        sticky-z-index="999"
      >
        <vs-button
          class="w-full"
          color="primary"
          icon-pack="feather"
          :disabled="isSavingCourse"
          @click="preSave"
          >Save</vs-button
        >
      </div>
      <div class="mt-10 relative" v-if="localCourse.avg_rating">
        <label class="vs-input--label"
          >Rating average: <strong>{{ localCourse.avg_rating }}</strong> -
          <span @click="sidebarReviews = true" class="cursor-pointer underline"
            >See details</span
          ></label
        >
        <rating-stars
          ref="courseStars"
          class="mt-3"
          @mouseenter.native="
            showStarWidget('courseStars', localCourse.id, 'course')
          "
          @mouseleave.native="widgetActive = false"
          :rating="
            localCourse.avg_rating
              ? parseFloat(localCourse.avg_rating)
              : localCourse.avg_rating
          "
          :starSize="30"
        />
      </div>
      <div class="mt-5 md:mt-10 media-files">
        <label class="vs-input--label">Background Image</label>
        <vx-input-group class="w-full mb-8" v-if="localCourse.images">
          <template slot="prepend" v-if="localCourse.images.main_image_url">
            <div class="prepend-text">
              <img
                class="course-image"
                :src="localCourse.images.main_image_url"
              />
            </div>
          </template>
          <vs-input disabled v-model="localCourse.images.main_image_url" />
        </vx-input-group>
        <div class="mt-5 md:mt-10 relative">
          <label class="vs-input--label">Course Trailer</label>
          <vs-input
            class="w-full"
            v-model="localCourse.trailer_jwplayer_id"
            description-text="JWPlayer media id, Example: 'mcAbYS1s'"
          ></vs-input>
          <div v-if="localCourse.trailer_jwplayer_id">
            <video-preview :mediaId="localCourse.trailer_jwplayer_id" />
          </div>
        </div>

        <div class="mt-5 md:mt-10 files-import" v-if="isEditing">
          <h4 class="mb-3">Attachments</h4>
          <div class="flex flex-wrap mt-10">
            <vs-alert
              color="warning"
              icon-pack="feather"
              icon="icon-info"
              class="w-full"
              v-if="rememberToSaveFiles"
            >
              Please remember to save the course after add or delete a file.
            </vs-alert>
            <FilesAttachments
              type="course"
              :files="getCurrentCourseFiles"
              :loading="filesLoading"
              :content="getCurrentCourse"
              @file-deleted="deleteFileFromCourse"
              @file-added="addFileToCourse"
              @file-changed="fileChanged"
            />
          </div>
        </div>
        <div class="mt-5 md:mt-10 flex flex-wrap add-passes" v-if="isEditing">
          <passes-selector
            v-if="this.localCourse.id"
            :passes="this.localCourse.direct_passes"
            type="Course"
            :entityId="localCourse.id"
          />
        </div>
        <div class="learning-paths" v-if="isEditing">
          <ContentTable
            :data="LPData"
            type="Learning Paths"
            relatedWith="Course"
          />
        </div>
      </div>
    </div>
    <div class="right-sidebar">
      <edit-leaf
        :leaftype="selectedType"
        :id="selectedId"
        :active="sidebarActive"
        :is-course-context="true"
        @change-sidebar="closeSidebar"
        @prevent-close="preventToClose"
        @add-to-structure="addLeafToStructure"
      />
      <rating-details
        v-if="localCourse.avg_rating"
        :courseId="localCourse.id"
        :active="sidebarReviews"
        @change-sidebar="closeReviewsSidebar"
      />
    </div>
    <rating-widget
      @mouseenter.native="widgetHover = true"
      @mouseleave.native="widgetHover = false"
      v-if="(widgetActive || widgetHover) && localCourse.avg_rating"
      :style="widgetPosition"
      :content_id="widgetContentId"
      :content_type="widgetContentType"
    />
    <UnsavedChangesPopup
      :active="showUnsavedChangesModal"
      @close="closeUnsavedChangesModal"
      @save="saveAndLeaveFromUnsavedChangesModal"
      @discard="discardAndLeaveFromUnsavedChangesModal"
    />
  </div>
</template>
<script>
import vSelect from 'vue-select'
import Sticky from 'vue-sticky-directive'
import 'flatpickr/dist/flatpickr.css'
import slug from 'slug-generator'
import cloneDeep from 'lodash.clonedeep'
import isEqual from 'lodash.isequal'
import debounce from 'lodash.debounce'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import UploadFile from '../components/UploadFile'
import EditLeaf from './forms/EditLeaf'
import CourseStructure from './forms/TreeStructure'
import { flatten, normalizeBusinessAccountIds } from '@/utils/utils.js'
import {
  getLoadedEnterpriseAccountIdsSet as getLoadedEnterpriseAccountIdsSetUtil,
  getMissingSelectedEnterpriseAccountIds as getMissingSelectedEnterpriseAccountIdsUtil,
  mergeEnterpriseAccountOptionsInPlace,
  normalizeEnterpriseAccountId as normalizeEnterpriseAccountIdUtil,
  normalizeEnterpriseAccountOption as normalizeEnterpriseAccountOptionUtil,
  normalizeEnterpriseAccountsPage as normalizeEnterpriseAccountsPageUtil,
  normalizeEnterpriseAccountsSearchTerm as normalizeEnterpriseAccountsSearchTermUtil,
} from '@/utils/enterpriseAccounts'
import {
  buildAudienceTypePayload,
  CourseSaveFlowError,
  getAccountAssignmentDelta,
} from '@/services/contentManager.service'
import {
  decorateLatestDialog,
  normalizeCustomContentAudienceType,
  shouldConfirmCustomToPublicTransition,
  shouldDisableCustomContentToggle,
} from '@/services/customContentToggle.service'
import Skills from '@/components/Skills'
import CONSTANTS from '@/constants'
import {
  COURSE_SAVE_FLOW_ERROR_NOTIFICATIONS,
  CUSTOM_CONTENT_ACCOUNT_PAGE_SIZE,
  CUSTOM_CONTENT_AUDIENCE_TYPES,
  CUSTOM_CONTENT_STRINGS,
} from '@/constants/customContent'
import {
  searchTags,
  getBasicData,
  starsWidget,
  searchBootcamps,
  buildUnsavedChangesNavigationHandlers,
  unsavedChangesNavigation,
} from '@/utils/mixins'
import FilesAttachments from '@/components/FilesEngineAttachments'
import PassesSelector from './forms/PassesSelector'
import VideoPreview from '@/components/VideoPreview'
import ContentTable from './components/ContentTable.vue'
import RatingStars from '@/components/RatingStars.vue'
import RatingWidget from '@/components/RatingWidget.vue'
import RatingDetails from './forms/RatingDetail.vue'
import UnsavedChangesPopup from '@/components/UnsavedChangesPopup.vue'

export default {
  mixins: [
    searchTags,
    getBasicData,
    starsWidget,
    searchBootcamps,
    unsavedChangesNavigation,
  ],
  directives: { Sticky },
  components: {
    'v-select': vSelect,
    Skills,
    UploadFile,
    EditLeaf,
    CourseStructure,
    FilesAttachments,
    PassesSelector,
    VideoPreview,
    ContentTable,
    RatingStars,
    RatingWidget,
    RatingDetails,
    UnsavedChangesPopup,
  },
  data() {
    return {
      CONTENT_TYPES: CONSTANTS.CONTENT_TYPES,
      selectedId: null,
      selectedType: null,
      selectedParent: null,
      localCourse: {},
      currentCategories: [],
      configdateTimePicker: {
        enableTime: true,
        dateFormat: 'm-d-Y H:i',
      },
      autoAlias: true,
      sidebarActive: false,
      importCsvErrors: null,
      rememberToSave: false,
      rememberToSaveFiles: false,
      preventToCloseSidebar: false,
      searchingRelatedOf: {
        loading: false,
        group: null,
      },
      LPData: null,
      starsConfig: {
        rating: 0,
        isIndicatorActive: false,
        style: {
          fullStarColor: '#ed8a19',
          emptyStarColor: '#737373',
          starWidth: 30,
          starHeight: 30,
        },
      },
      filesLoading: false,
      sidebarReviews: false,
      bootcampOptions: [],
      myIneUrl: process.env.VUE_APP_MY_INE_URL,
      isSavingCourse: false,
      customContentStrings: CUSTOM_CONTENT_STRINGS,
      enterpriseAccountOptions: [],
      enterpriseAccountsLoading: false,
      enterpriseAccountsValidationError: '',
      searchEnterpriseAccountsDebounced: null,
      enterpriseAccountsSearchTerm: '',
      enterpriseAccountsNextPage: null,
      enterpriseAccountsGlobalNextPage: null,
      enterpriseAccountsAllPagesLoaded: false,
      enterpriseAccountsNoResults: false,
      enterpriseAccountsSearchRequestId: 0,
      enterpriseAccountsInFlightPages: [],
      enterpriseAccountsLoadedPageNumbers: [],
      enterpriseAccountsDropdownMenu: null,
      enterpriseAccountsDropdownActive: false,
      persistedCustomContentAudienceType: CUSTOM_CONTENT_AUDIENCE_TYPES.CUSTOM,
      persistedCourseSnapshot: null,
    }
  },
  computed: {
    ...mapGetters('app', ['getSelectedCompany']),
    ...mapGetters('featureFlags', ['customContentCoursesEnabled']),
    ...mapGetters('vendors', ['getVendors']),
    ...mapGetters('instructors', ['getInstructors']),
    ...mapGetters('categories', ['getCategories']),
    ...mapGetters('lareas', ['getLAreas']),
    ...mapGetters('videos', ['getVideoEntries', 'getCurrentVideo']),
    ...mapGetters('quizzes', ['getQuizEntries', 'getCurrentQuiz']),
    ...mapGetters('exercises', ['getExerciseEntries']),
    ...mapGetters('labs', ['getLabEntries']),
    ...mapGetters('iframes', ['getIFrameEntries']),
    ...mapGetters('courses', [
      'getCurrentCourse',
      'getCoursePossibleStatus',
      'getCourseDifficulties',
      'getCourseStructure',
      'getStatusTransitions',
      'getCurrentCourseFiles',
    ]),
    canUploadCSV() {
      return (
        this.isEditing &&
        this.localCourse.content &&
        !this.localCourse.content.length
      )
    },
    isEditing() {
      return this.$route.name === 'edit-course'
    },
    currentSavedAudienceType() {
      return normalizeCustomContentAudienceType(
        this.persistedCustomContentAudienceType
      )
    },
    isCustomContentToggleDisabled() {
      return shouldDisableCustomContentToggle({
        featureEnabled: this.customContentCoursesEnabled,
        isEditing: this.isEditing,
        persistedAudienceType: this.currentSavedAudienceType,
      })
    },
    customContentToggleTooltip() {
      if (!this.isCustomContentToggleDisabled) return null
      return this.customContentStrings.publicToCustomTooltip
    },
    selectedEnterpriseAccountIds() {
      return normalizeBusinessAccountIds(this.localCourse.business_account_ids)
    },
    selectedEnterpriseAccountIdsSet() {
      return new Set(this.selectedEnterpriseAccountIds)
    },
    isEnterpriseAccountsFeatureActive() {
      return this.customContentCoursesEnabled && this.isCustomContentEnabled
    },
    enterpriseAccountOptionsById() {
      const optionsMap = this.enterpriseAccountOptions.reduce((acc, option) => {
        if (!option || typeof option !== 'object') return acc

        const optionId =
          option.value !== undefined && option.value !== null
            ? option.value
            : option.id
        const normalizedId = this.normalizeEnterpriseAccountId(optionId)
        if (!normalizedId) return acc

        acc[normalizedId] = option
        return acc
      }, {})

      this.selectedEnterpriseAccountIds.forEach((accountId) => {
        if (!optionsMap[accountId]) {
          optionsMap[accountId] = {
            id: accountId,
            value: accountId,
            label: accountId,
            name: accountId,
          }
        }
      })

      return optionsMap
    },
    visibleEnterpriseAccountOptions() {
      const mergedOptions = Object.values(this.enterpriseAccountOptionsById)
      const normalizedSearchTerm = this.normalizeEnterpriseAccountsSearchTerm(
        this.enterpriseAccountsSearchTerm
      )
      const visibleOptions = mergedOptions.filter((option) => {
        const optionId = String(option.value || '').trim()
        return !this.selectedEnterpriseAccountIdsSet.has(optionId)
      })

      if (!normalizedSearchTerm) {
        return visibleOptions
      }

      return visibleOptions.filter((option) =>
        String(option.label || '')
          .toLowerCase()
          .includes(normalizedSearchTerm)
      )
    },
    enterpriseAccountDropdownOptions() {
      if (this.enterpriseAccountsSearchTerm) {
        return this.visibleEnterpriseAccountOptions
      }
      return Object.values(this.enterpriseAccountOptionsById)
    },
    enterpriseAccountsNoOptionsText() {
      if (this.enterpriseAccountsLoading) {
        return ''
      }
      if (this.showEnterpriseAccountsNoResults) {
        return this.customContentStrings.enterpriseAccountsNoMatches
      }
      if (
        this.enterpriseAccountsSearchTerm &&
        this.enterpriseAccountsNextPage
      ) {
        return ''
      }
      return this.customContentStrings.enterpriseAccountsNoOptionsEnabled
    },
    showEnterpriseAccountsNoResults() {
      const hasSearchTerm = Boolean(
        String(this.enterpriseAccountsSearchTerm || '').trim()
      )
      const hasVisibleOptions = this.visibleEnterpriseAccountOptions.length > 0

      return (
        hasSearchTerm &&
        !hasVisibleOptions &&
        this.enterpriseAccountsAllPagesLoaded &&
        this.enterpriseAccountsNoResults &&
        !this.enterpriseAccountsLoading
      )
    },
    mainLAreaOptions() {
      try {
        if (this.getLAreas && this.localCourse.learning_areas) {
          return this.getLAreas.filter((la) =>
            this.localCourse.learning_areas.includes(la.value)
          )
        } else return []
      } catch (error) {
        return []
      }
    },
    isCustomContentEnabled: {
      get() {
        return (
          this.localCourse.audience_type === CUSTOM_CONTENT_AUDIENCE_TYPES.CUSTOM
        )
      },
      set(value) {
        if (value === this.isCustomContentEnabled) return
        if (value && this.isCustomContentToggleDisabled) return

        if (
          shouldConfirmCustomToPublicTransition({
            featureEnabled: this.customContentCoursesEnabled,
            isEditing: this.isEditing,
            persistedAudienceType: this.currentSavedAudienceType,
            nextIsCustomContentEnabled: value,
          })
        ) {
          this.openConvertToPublicConfirmationDialog(
            () => {
              this.applyCustomContentToggle(value)
            },
            () => {
              this.localCourse = {
                ...this.localCourse,
                ...buildAudienceTypePayload(true),
              }
            }
          )
          return
        }

        this.applyCustomContentToggle(value)
      },
    },
  },
  methods: {
    ...mapMutations('courses', {
      changeFileInCourse: 'CHANGE_FILE_IN_COURSE',
      resetFilesInCourse: 'RESET_FILES_IN_COURSE',
    }),
    ...mapActions('videos', [
      'getAllVideos',
      'saveVideoToEdit',
      'resetCurrentVideo',
    ]),
    ...mapActions('quizzes', [
      'getAllQuizzes',
      'getQuizAction',
      'saveQuizToEdit',
      'resetCurrentQuiz',
    ]),
    ...mapActions('exercises', [
      'getAllExercises',
      'saveExerciseToEdit',
      'resetCurrentExercise',
    ]),
    ...mapActions('labs', [
      'getAllLabs',
      'getLabAction',
      'fetchObjectives',
      'saveLabToEdit',
      'resetCurrentLab',
    ]),
    ...mapActions('iframes', [
      'getAllIFrames',
      'saveIFrameToEdit',
      'resetCurrentIFrame',
    ]),
    ...mapActions('lpaths', ['searchLPaths']),
    ...mapActions('bootcamps', ['searchBootcamps']),
    ...mapActions('courses', [
      'getCourseAction',
      'resetCurrentCourse',
      'saveCourseToApi',
      'uploadCourseCSV',
      'saveCourseToStore',
      'addLeafToContentStructure',
      'addLeafsBetweenContentStructure',
      'removeLeafToContentStructure',
      'addGroupToContentStructure',
      'removeGroupToContentStructure',
      'renameGroupOfContentStructure',
      'deleteContentStructure',
      'searchRelatedContent',
      'getAllCourseFiles',
      'searchBusinessAccounts',
      'getBusinessAccount',
    ]),
    setPersistedCustomContentAudienceType(course = {}) {
      this.persistedCustomContentAudienceType = normalizeCustomContentAudienceType(
        course.audience_type
      )
    },
    setPersistedCourseSnapshot(course = this.localCourse) {
      this.persistedCourseSnapshot = this.normalizeCustomContentFields(
        cloneDeep(course || {})
      )
    },
    hasUnsavedCourseChanges() {
      if (!this.isEditing) return false
      if (this.rememberToSave || this.rememberToSaveFiles) return true
      if (!this.persistedCourseSnapshot) return false

      const normalizedLocalCourse = this.normalizeCustomContentFields(
        cloneDeep(this.localCourse || {})
      )
      return !isEqual(normalizedLocalCourse, this.persistedCourseSnapshot)
    },
    ...buildUnsavedChangesNavigationHandlers({
      hasUnsavedChangesMethod: 'hasUnsavedCourseChanges',
      saveForNavigationMethod: 'saveCourseForPendingRouteNavigation',
      restoreForDiscardMethod: 'restoreCourseSnapshotForDiscard',
    }),
    async saveCourseForPendingRouteNavigation() {
      const didSave = await this.preSave({
        onAbort: () => {
          this.pendingLeaveRoute = null
        },
      })
      if (!didSave) {
        this.pendingLeaveRoute = null
        return false
      }
      return true
    },
    restoreCourseSnapshotForDiscard() {
      if (!this.persistedCourseSnapshot) return
      const restoredCourse = this.normalizeCustomContentFields(
        cloneDeep(this.persistedCourseSnapshot)
      )
      this.localCourse = restoredCourse
      this.saveCourseToStore(restoredCourse)
      this.setPersistedCustomContentAudienceType(restoredCourse)
      this.getRelatedLPs()
      this.rememberToSave = false
      this.rememberToSaveFiles = false
      this.enterpriseAccountsValidationError = ''
    },
    applyRouteLeaveState(to) {
      if (to.name === 'create-course') {
        this.resetCurrentCourse()
      }
      if (to.name === 'edit-course') {
        this.getCourseToEdit(to.params.id)
      }
      this.hydrateLocalCourse(this.getCurrentCourse)
      this.getRelatedLPs()
    },
    applyCustomContentToggle(value) {
      this.localCourse = {
        ...this.localCourse,
        ...buildAudienceTypePayload(value),
      }
      this.enterpriseAccountsValidationError = ''

      if (!value) {
        this.cancelSearchEnterpriseAccountsDebounced()
        this.enterpriseAccountsSearchRequestId += 1
        this.enterpriseAccountsLoading = false
      }
    },
    normalizeCustomContentFields(course = {}) {
      const {
        assigned_accounts: legacyAssignedAccounts,
        ...courseData
      } = course
      const normalizedAccessType =
        course.audience_type === CUSTOM_CONTENT_AUDIENCE_TYPES.CUSTOM
          ? CUSTOM_CONTENT_AUDIENCE_TYPES.CUSTOM
          : CUSTOM_CONTENT_AUDIENCE_TYPES.PUBLIC
      const normalizedBusinessAccountIds = normalizeBusinessAccountIds(
        Array.isArray(courseData.business_account_ids)
          ? courseData.business_account_ids
          : legacyAssignedAccounts
      )
      return {
        ...courseData,
        audience_type: normalizedAccessType,
        business_account_ids: normalizedBusinessAccountIds,
      }
    },
    hydrateLocalCourse(course = {}, { autoAlias = !course.id } = {}) {
      this.autoAlias = autoAlias
      this.localCourse = this.normalizeCustomContentFields(cloneDeep(course))
    },
    notifyCustomContentError(text) {
      this.$vs.notify({
        title: 'Error',
        text,
        iconPack: 'feather',
        icon: 'icon-alert-circle',
        color: 'danger',
        time: '3500',
      })
    },
    notifyEnterpriseAccountsLoadError() {
      this.notifyCustomContentError(
        this.customContentStrings.enterpriseAccountsLoadError
      )
    },
    normalizeEnterpriseAccountId(value) {
      return normalizeEnterpriseAccountIdUtil(value)
    },
    normalizeEnterpriseAccountOption(account) {
      return normalizeEnterpriseAccountOptionUtil(account)
    },
    reduceEnterpriseAccountOption(account) {
      if (!account) return null

      if (typeof account === 'string' || typeof account === 'number') {
        return this.normalizeEnterpriseAccountId(account)
      }

      if (typeof account !== 'object') return null

      const optionValue =
        account.value !== undefined && account.value !== null
          ? account.value
          : account.id
      return this.normalizeEnterpriseAccountId(optionValue)
    },
    getEnterpriseAccountOptionLabel(option) {
      const optionId =
        typeof option === 'object' && option !== null
          ? option.value || option.id
          : option
      const normalizedId = this.normalizeEnterpriseAccountId(optionId)
      if (!normalizedId) return ''

      const optionById = this.enterpriseAccountOptionsById[normalizedId] || null
      return (
        (option && option.label) ||
        (option && option.name) ||
        (optionById && optionById.label) ||
        (optionById && optionById.name) ||
        normalizedId
      )
    },
    mergeEnterpriseAccountOptions(accounts = []) {
      if (!Array.isArray(this.enterpriseAccountOptions)) {
        this.enterpriseAccountOptions = []
      }

      mergeEnterpriseAccountOptionsInPlace(this.enterpriseAccountOptions, accounts)
    },
    // Build a fast lookup set from currently loaded dropdown options.
    // We use this to avoid refetching account records we already have labels for.
    getLoadedEnterpriseAccountIdsSet() {
      return getLoadedEnterpriseAccountIdsSetUtil(this.enterpriseAccountOptions)
    },
    // Return only selected IDs that are missing from the current option list.
    // This is the core of the hydration fix: fetch just the gaps, not everything.
    getMissingSelectedEnterpriseAccountIds(ids = []) {
      return getMissingSelectedEnterpriseAccountIdsUtil(
        ids,
        this.enterpriseAccountOptions
      )
    },
    getEnterpriseAccountsPageFromUrl(url) {
      if (!url) return null
      const pageMatch = `${url}`.match(/[?&]page=(\d+)/)
      return pageMatch && pageMatch[1] ? pageMatch[1] : null
    },
    getEnterpriseAccountsPageSize() {
      return String(CUSTOM_CONTENT_ACCOUNT_PAGE_SIZE)
    },
    normalizeEnterpriseAccountsSearchTerm(searchTerm = '') {
      return normalizeEnterpriseAccountsSearchTermUtil(searchTerm)
    },
    normalizeEnterpriseAccountsPage(page = null) {
      return normalizeEnterpriseAccountsPageUtil(page)
    },
    hasEnterpriseAccountsPageLoaded(page = null) {
      const normalizedPage = this.normalizeEnterpriseAccountsPage(page)
      if (!normalizedPage) return false
      return this.enterpriseAccountsLoadedPageNumbers.includes(normalizedPage)
    },
    markEnterpriseAccountsPageLoaded(page = null) {
      const normalizedPage = this.normalizeEnterpriseAccountsPage(page)
      if (!normalizedPage) return
      if (!this.hasEnterpriseAccountsPageLoaded(normalizedPage)) {
        this.enterpriseAccountsLoadedPageNumbers = [
          ...this.enterpriseAccountsLoadedPageNumbers,
          normalizedPage,
        ]
      }
    },
    isEnterpriseAccountsPageInFlight(page = null) {
      const normalizedPage = this.normalizeEnterpriseAccountsPage(page)
      if (!normalizedPage) return false
      return this.enterpriseAccountsInFlightPages.includes(normalizedPage)
    },
    markEnterpriseAccountsPageInFlight(page = null) {
      const normalizedPage = this.normalizeEnterpriseAccountsPage(page)
      if (!normalizedPage) return
      if (!this.isEnterpriseAccountsPageInFlight(normalizedPage)) {
        this.enterpriseAccountsInFlightPages = [
          ...this.enterpriseAccountsInFlightPages,
          normalizedPage,
        ]
      }
    },
    clearEnterpriseAccountsPageInFlight(page = null) {
      const normalizedPage = this.normalizeEnterpriseAccountsPage(page)
      if (!normalizedPage) return
      this.enterpriseAccountsInFlightPages = this.enterpriseAccountsInFlightPages.filter(
        (inFlightPage) => inFlightPage !== normalizedPage
      )
    },
    getEnterpriseAccountLocalMatches(searchTerm = this.enterpriseAccountsSearchTerm) {
      const normalizedSearchTerm = this.normalizeEnterpriseAccountsSearchTerm(
        searchTerm
      )
      const cachedOptions = Object.values(this.enterpriseAccountOptionsById)

      if (!normalizedSearchTerm) {
        return cachedOptions
      }

      return cachedOptions.filter((option) => {
        const searchableLabel = String(
          option.label || option.name || option.value || option.id || ''
        ).toLowerCase()

        return searchableLabel.includes(normalizedSearchTerm)
      })
    },
    filterOutSelectedEnterpriseAccounts(options = []) {
      return options.filter((option) => {
        const optionId = this.normalizeEnterpriseAccountId(option.value || option.id)
        return optionId && !this.selectedEnterpriseAccountIdsSet.has(optionId)
      })
    },
    getVisibleEnterpriseAccountLocalMatches(
      searchTerm = this.enterpriseAccountsSearchTerm
    ) {
      return this.filterOutSelectedEnterpriseAccounts(
        this.getEnterpriseAccountLocalMatches(searchTerm)
      )
    },
    syncEnterpriseAccountsNoResults(searchTerm = this.enterpriseAccountsSearchTerm) {
      const hasSearchTerm = Boolean(
        this.normalizeEnterpriseAccountsSearchTerm(searchTerm)
      )
      const hasVisibleMatches =
        this.getVisibleEnterpriseAccountLocalMatches(searchTerm).length > 0

      this.enterpriseAccountsNoResults =
        hasSearchTerm &&
        !hasVisibleMatches &&
        this.enterpriseAccountsAllPagesLoaded &&
        !this.enterpriseAccountsLoading
    },
    resetEnterpriseAccountsSearchState({
      dropdownActive = this.enterpriseAccountsDropdownActive,
      nextPage = this.enterpriseAccountsNextPage,
      incrementRequestId = true,
    } = {}) {
      this.enterpriseAccountsDropdownActive = dropdownActive
      if (incrementRequestId) {
        this.enterpriseAccountsSearchRequestId += 1
      }
      this.cancelSearchEnterpriseAccountsDebounced()
      this.enterpriseAccountsSearchTerm = ''
      this.enterpriseAccountsNoResults = false
      this.enterpriseAccountsNextPage = nextPage
    },
    resetEnterpriseAccountsFeatureState() {
      this.resetEnterpriseAccountsSearchState({
        dropdownActive: false,
        nextPage: null,
      })
      this.detachEnterpriseAccountsScrollListener()
      this.enterpriseAccountsLoading = false
      this.enterpriseAccountsGlobalNextPage = null
      this.enterpriseAccountsAllPagesLoaded = false
      this.enterpriseAccountsInFlightPages = []
      this.enterpriseAccountsLoadedPageNumbers = []
      this.enterpriseAccountsValidationError = ''
    },
    shouldDeferEnterpriseAccountsSearchUntilInitialPageLoad(searchTerm = '') {
      const normalizedSearchTerm = this.normalizeEnterpriseAccountsSearchTerm(
        searchTerm
      )
      if (!normalizedSearchTerm) return false

      return (
        !this.hasEnterpriseAccountsPageLoaded('1') &&
        this.isEnterpriseAccountsPageInFlight('1')
      )
    },
    async continueEnterpriseAccountsSearchIfNeeded(
      searchTerm = this.enterpriseAccountsSearchTerm,
      requestId = this.enterpriseAccountsSearchRequestId
    ) {
      const normalizedSearchTerm = this.normalizeEnterpriseAccountsSearchTerm(
        searchTerm
      )
      if (!normalizedSearchTerm) return
      if (requestId !== this.enterpriseAccountsSearchRequestId) return

      const hasVisibleMatches =
        this.getVisibleEnterpriseAccountLocalMatches(normalizedSearchTerm).length >
        0
      if (hasVisibleMatches) {
        this.enterpriseAccountsNoResults = false
        return
      }

      if (!this.enterpriseAccountsNextPage || this.enterpriseAccountsAllPagesLoaded) {
        this.syncEnterpriseAccountsNoResults(normalizedSearchTerm)
        return
      }

      await this.loadEnterpriseAccountsUntilLocalMatchOrExhaustion(
        normalizedSearchTerm,
        requestId
      )
    },
    enterpriseAccountsDropdownShouldOpen({ noDrop, open }) {
      const hasSearchTerm = Boolean(
        String(this.enterpriseAccountsSearchTerm || '').trim()
      )
      const keepOpenForSearchResults =
        this.enterpriseAccountsDropdownActive &&
        hasSearchTerm &&
        !this.enterpriseAccountsLoading &&
        !this.enterpriseAccountsNextPage &&
        this.visibleEnterpriseAccountOptions.length === 0

      if (keepOpenForSearchResults) {
        return true
      }

      if (this.enterpriseAccountsDropdownActive && hasSearchTerm) {
        return true
      }

      return !noDrop && open
    },
    async fetchEnterpriseAccountsPage({
      page = this.enterpriseAccountsNextPage,
      skipIfLoaded = true,
    } = {}) {
      const normalizedPage = this.normalizeEnterpriseAccountsPage(page)
      if (!normalizedPage) return { skipped: true }
      if (this.enterpriseAccountsAllPagesLoaded) return { skipped: true }
      if (skipIfLoaded && this.hasEnterpriseAccountsPageLoaded(normalizedPage)) {
        return { skipped: true }
      }
      if (this.isEnterpriseAccountsPageInFlight(normalizedPage)) {
        return { skipped: true }
      }

      this.markEnterpriseAccountsPageInFlight(normalizedPage)
      this.enterpriseAccountsLoading = true
      let fetchResult = { skipped: true }
      try {
        const response = await this.searchBusinessAccounts({
          page: normalizedPage,
          page_size: this.getEnterpriseAccountsPageSize(),
        })

        this.markEnterpriseAccountsPageLoaded(normalizedPage)
        this.mergeEnterpriseAccountOptions(response.results || [])

        const nextPage = this.getEnterpriseAccountsPageFromUrl(response.next)
        this.enterpriseAccountsNextPage = nextPage
        this.enterpriseAccountsGlobalNextPage = nextPage
        this.enterpriseAccountsAllPagesLoaded = !nextPage
        this.syncEnterpriseAccountsNoResults(this.enterpriseAccountsSearchTerm)

        fetchResult = { skipped: false, response, nextPage }
      } finally {
        this.clearEnterpriseAccountsPageInFlight(normalizedPage)
        if (!this.enterpriseAccountsInFlightPages.length) {
          this.enterpriseAccountsLoading = false
        }
      }

      if (normalizedPage === '1' && !fetchResult.skipped) {
        await this.continueEnterpriseAccountsSearchIfNeeded(
          this.enterpriseAccountsSearchTerm,
          this.enterpriseAccountsSearchRequestId
        )
      }

      return fetchResult
    },
    async ensureSelectedEnterpriseAccountsLoaded(ids = []) {
      const missingSelectedIds = this.getMissingSelectedEnterpriseAccountIds(ids)
      if (!missingSelectedIds.length) return

      const hydratedAccounts = await Promise.all(
        missingSelectedIds.map((accountId) =>
          this.getBusinessAccount({ accountId }).catch(() => null)
        )
      )

      this.mergeEnterpriseAccountOptions(hydratedAccounts.filter(Boolean))
    },
    async loadEnterpriseAccountsList({ ids = [] } = {}) {
      if (!this.isEnterpriseAccountsFeatureActive) {
        return
      }
      try {
        await this.fetchEnterpriseAccountsPage({
          page: '1',
          skipIfLoaded: true,
        })
        await this.ensureSelectedEnterpriseAccountsLoaded(ids)
      } catch (error) {
        this.notifyEnterpriseAccountsLoadError()
      }
    },
    async loadNextEnterpriseAccountsPage(page = this.enterpriseAccountsNextPage) {
      const nextPageToLoad = this.normalizeEnterpriseAccountsPage(page)
      if (!nextPageToLoad) {
        return
      }
      if (this.enterpriseAccountsLoading) {
        return
      }
      if (this.enterpriseAccountsAllPagesLoaded) {
        return
      }
      try {
        await this.fetchEnterpriseAccountsPage({
          page: nextPageToLoad,
          skipIfLoaded: true,
        })
      } catch (error) {
        this.notifyEnterpriseAccountsLoadError()
      }
    },
    async loadEnterpriseAccountsUntilLocalMatchOrExhaustion(
      searchTerm,
      requestId = this.enterpriseAccountsSearchRequestId
    ) {
      const normalizedSearchTerm = this.normalizeEnterpriseAccountsSearchTerm(
        searchTerm
      )
      if (!normalizedSearchTerm) return

      const visitedPages = new Set()
      let safetyCounter = 0

      while (
        this.enterpriseAccountsNextPage &&
        !this.enterpriseAccountsAllPagesLoaded &&
        safetyCounter < 100
      ) {
        if (requestId !== this.enterpriseAccountsSearchRequestId) return

        const currentPage = String(this.enterpriseAccountsNextPage)
        if (visitedPages.has(currentPage)) return
        visitedPages.add(currentPage)

        await this.loadNextEnterpriseAccountsPage(currentPage)
        if (requestId !== this.enterpriseAccountsSearchRequestId) return
        safetyCounter += 1
        const hasMatch =
          this.getVisibleEnterpriseAccountLocalMatches(normalizedSearchTerm).length >
          0
        if (hasMatch) {
          this.enterpriseAccountsNoResults = false
          return
        }

        if (String(this.enterpriseAccountsNextPage || '') === currentPage) return
      }

      this.syncEnterpriseAccountsNoResults(normalizedSearchTerm)
    },
    async loadMoreEnterpriseAccountsUntilMatch(
      searchTerm,
      requestId = this.enterpriseAccountsSearchRequestId
    ) {
      await this.loadEnterpriseAccountsUntilLocalMatchOrExhaustion(
        searchTerm,
        requestId
      )
    },
    async searchEnterpriseAccountsUntilMatch(
      searchTerm,
      requestId = this.enterpriseAccountsSearchRequestId
    ) {
      await this.loadEnterpriseAccountsUntilLocalMatchOrExhaustion(
        searchTerm,
        requestId
      )
    },
    getEnterpriseAccountsDropdownMenuElement() {
      const selectRef = this.$refs.enterpriseAccountsSelect
      if (!selectRef) return null

      if (
        selectRef.$refs &&
        selectRef.$refs.dropdownMenu &&
        selectRef.$refs.dropdownMenu.classList &&
        selectRef.$refs.dropdownMenu.classList.contains('vs__dropdown-menu')
      ) {
        return selectRef.$refs.dropdownMenu
      }

      if (selectRef.uid) {
        const menuById = document.getElementById(`vs${selectRef.uid}__listbox`)
        if (
          menuById &&
          menuById.classList &&
          menuById.classList.contains('vs__dropdown-menu')
        ) {
          return menuById
        }
      }

      return null
    },
    attachEnterpriseAccountsScrollListener() {
      this.$nextTick(() => {
        const menuElement = this.getEnterpriseAccountsDropdownMenuElement()
        if (!menuElement) return

        if (this.enterpriseAccountsDropdownMenu === menuElement) return
        this.detachEnterpriseAccountsScrollListener()
        this.enterpriseAccountsDropdownMenu = menuElement
        menuElement.addEventListener('scroll', this.onEnterpriseAccountsScroll, {
          passive: true,
        })
      })
    },
    detachEnterpriseAccountsScrollListener() {
      if (!this.enterpriseAccountsDropdownMenu) return
      this.enterpriseAccountsDropdownMenu.removeEventListener(
        'scroll',
        this.onEnterpriseAccountsScroll
      )
      this.enterpriseAccountsDropdownMenu = null
    },
    async onEnterpriseAccountsScroll(event) {
      if (!this.isEnterpriseAccountsFeatureActive) {
        return
      }

      const target = event && event.target
      if (!target) return

      const nearBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 20
      if (!nearBottom) return

      await this.loadNextEnterpriseAccountsPage()
      this.syncEnterpriseAccountsNoResults(this.enterpriseAccountsSearchTerm)
    },
    cancelSearchEnterpriseAccountsDebounced() {
      if (
        this.searchEnterpriseAccountsDebounced &&
        typeof this.searchEnterpriseAccountsDebounced.cancel === 'function'
      ) {
        this.searchEnterpriseAccountsDebounced.cancel()
      }
    },
    searchEnterpriseAccountsLocally(searchTerm = '') {
      this.enterpriseAccountsSearchTerm = this.normalizeEnterpriseAccountsSearchTerm(
        searchTerm
      )
      this.syncEnterpriseAccountsNoResults(this.enterpriseAccountsSearchTerm)
    },
    async onOpenEnterpriseAccounts() {
      if (!this.isEnterpriseAccountsFeatureActive) {
        return
      }
      this.resetEnterpriseAccountsSearchState({
        dropdownActive: true,
        nextPage:
          this.enterpriseAccountsGlobalNextPage || this.enterpriseAccountsNextPage,
      })
      const shouldLoadBrowsePage =
        !this.enterpriseAccountsAllPagesLoaded &&
        !this.hasEnterpriseAccountsPageLoaded('1')

      if (shouldLoadBrowsePage) {
        await this.loadEnterpriseAccountsList({
          ids: this.localCourse.business_account_ids,
        })
      }
      this.attachEnterpriseAccountsScrollListener()
    },
    onCloseEnterpriseAccounts() {
      this.resetEnterpriseAccountsSearchState({
        dropdownActive: false,
        nextPage: this.enterpriseAccountsGlobalNextPage,
      })
      this.detachEnterpriseAccountsScrollListener()
    },
    onSearchEnterpriseAccounts(search, loading) {
      const updateLoading =
        typeof loading === 'function' ? loading : () => {}
      if (!this.isEnterpriseAccountsFeatureActive) {
        this.resetEnterpriseAccountsSearchState({
          dropdownActive: false,
          nextPage: this.enterpriseAccountsGlobalNextPage,
        })
        updateLoading(false)
        return
      }

      const normalizedSearchTerm = this.normalizeEnterpriseAccountsSearchTerm(
        search
      )
      this.enterpriseAccountsDropdownActive = true
      this.enterpriseAccountsSearchRequestId += 1
      const currentSearchRequestId = this.enterpriseAccountsSearchRequestId
      this.enterpriseAccountsSearchTerm = normalizedSearchTerm
      this.enterpriseAccountsNoResults = false

      if (!normalizedSearchTerm) {
        this.resetEnterpriseAccountsSearchState({
          dropdownActive: true,
          nextPage: this.enterpriseAccountsGlobalNextPage,
          incrementRequestId: false,
        })
        this.syncEnterpriseAccountsNoResults('')
        updateLoading(false)
        return
      }

      const localMatches = this.getVisibleEnterpriseAccountLocalMatches(
        normalizedSearchTerm
      )
      if (localMatches.length > 0) {
        this.cancelSearchEnterpriseAccountsDebounced()
        this.enterpriseAccountsNoResults = false
        updateLoading(false)
        return
      }
      if (this.enterpriseAccountsAllPagesLoaded || !this.enterpriseAccountsNextPage) {
        if (
          !this.enterpriseAccountsAllPagesLoaded &&
          this.shouldDeferEnterpriseAccountsSearchUntilInitialPageLoad(
            normalizedSearchTerm
          )
        ) {
          this.enterpriseAccountsNoResults = false
          updateLoading(false)
          return
        }
        this.cancelSearchEnterpriseAccountsDebounced()
        this.syncEnterpriseAccountsNoResults(normalizedSearchTerm)
        updateLoading(false)
        return
      }

      updateLoading(true)
      this.searchEnterpriseAccountsDebounced(
        normalizedSearchTerm,
        updateLoading,
        currentSearchRequestId
      )
    },
    successUpload() {
      this.saveCourseToStore(this.localCourse)
    },
    errorUpload(error) {
      this.saveCourseToStore(this.localCourse)
      this.showError(error)
    },
    async uploadCourseCSVWrapper(formData) {
      try {
        this.$vs.loading()
        let response = await this.uploadCourseCSV(formData)
        await this.getCourseAction({ courseId: this.$route.params.id })
        this.hydrateLocalCourse(this.getCurrentCourse)
        this.getRelatedLPs()
        this.showUploadErrors(response.results)

        this.$vs.notify({
          color: 'success',
          title: 'Upload Success',
          text: 'The file was uploaded successfully',
        })

        const allStructureVideos = this.getDeepArrayIds(
          this.localCourse.content,
          'video'
        )
        if (allStructureVideos.length) {
          response = await this.searchRelatedContent({
            ids: allStructureVideos,
          })
          Object.entries(response).map(([key, items]) => {
            this.addLeafsBetweenContentStructure({
              afterOf: key,
              leafs: items.reduce((acc, item) => {
                item.related = true
                acc.push(item)
                return acc
              }, []),
            })
          })
        }
      } catch (error) {
        this.showError(error)
      }
      await this.getAllCourseItems()
      this.$vs.loading.close()
    },
    showUploadErrors(results) {
      this.importCsvErrors = results.reduce((acc, e) => {
        if (e.result === 'error') {
          if (typeof e.errors === 'string') {
            acc.push([e.errors, e.name])
          } else {
            acc.push([Object.values(e.errors).join(), e.s3_video_url])
          }
        }
        return acc
      }, [])
    },
    requiresAccountsRemovalConfirmation(previousCourse) {
      if (!this.customContentCoursesEnabled) return false
      if (!this.isEditing) return false

      const previousAudienceType =
        previousCourse.audience_type === CUSTOM_CONTENT_AUDIENCE_TYPES.CUSTOM
          ? CUSTOM_CONTENT_AUDIENCE_TYPES.CUSTOM
          : CUSTOM_CONTENT_AUDIENCE_TYPES.PUBLIC
      if (previousAudienceType !== CUSTOM_CONTENT_AUDIENCE_TYPES.CUSTOM) {
        return false
      }
      if (this.localCourse.audience_type !== CUSTOM_CONTENT_AUDIENCE_TYPES.CUSTOM) {
        return false
      }

      const initialAssignedAccountIds = Array.isArray(
        previousCourse.business_account_ids
      )
        ? previousCourse.business_account_ids
        : previousCourse.assigned_accounts

      const { removedAccountIds } = getAccountAssignmentDelta(
        initialAssignedAccountIds,
        this.localCourse.business_account_ids
      )
      return removedAccountIds.length > 0
    },
    openConvertToPublicConfirmationDialog(onAccept, onCancel = () => {}) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'primary',
        title: this.customContentStrings.convertToPublicTitle,
        text: this.customContentStrings.convertToPublicBody.trim(),
        acceptText: this.customContentStrings.convertToPublicAcceptText,
        cancelText: this.customContentStrings.convertToPublicCancelText,
        accept: onAccept,
        cancel: onCancel,
      })
      this.$nextTick(() => {
        decorateLatestDialog('custom-content-convert-dialog', {
          maxWidth: '500px',
          normalizeText: true,
        })
      })
    },
    openRemoveAccountsConfirmationDialog(onAccept, onCancel = () => {}) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'warning',
        title: this.customContentStrings.removeAccountsTitle,
        text: this.customContentStrings.removeAccountsBody,
        acceptText: this.customContentStrings.removeAccountsAcceptText,
        cancelText: this.customContentStrings.removeAccountsCancelText,
        accept: onAccept,
        cancel: onCancel,
      })
    },
    validateCustomContentBeforeSave() {
      this.enterpriseAccountsValidationError = ''
      return true
    },
    maybeHandleCustomContentConfirmations(
      previousCourse,
      onAccept,
      onCancel = () => {}
    ) {
      if (this.requiresAccountsRemovalConfirmation(previousCourse)) {
        this.openRemoveAccountsConfirmationDialog(onAccept, onCancel)
        return
      }

      onAccept()
    },
    async preSave({ onSuccess = () => {}, onAbort = () => {} } = {}) {
      if (this.isSavingCourse) {
        onAbort()
        return false
      }
      const previousCourse = cloneDeep(this.getCurrentCourse)

      const continueSave = () => {
        return this.confirmSave(previousCourse, { onSuccess, onAbort })
      }

      if (
        this.getCurrentCourse.status !== this.localCourse.status &&
        !this.getStatusTransitions[this.localCourse.status].includes(
          this.getCurrentCourse.status
        )
      ) {
        return new Promise((resolve) => {
          this.$vs.dialog({
            color: 'warning',
            title: `Warning`,
            text: `After saving you won't be able to return to ${this.getCurrentCourse.status} status`,
            accept: async () => {
              const didSave = await continueSave()
              resolve(didSave)
            },
            cancel: () => {
              onAbort()
              resolve(false)
            },
          })
        })
      }

      return continueSave()
    },
    confirmSave(previousCourse, { onSuccess = () => {}, onAbort = () => {} } = {}) {
      return new Promise((resolve) => {
        try {
          this.maybeHandleCustomContentConfirmations(
            previousCourse,
            async () => {
              try {
                const didSave = await this.saveLocalCourse(previousCourse, {
                  onSuccess,
                })
                if (didSave) {
                  resolve(true)
                  return
                }
              } catch (error) {
                this.showError(error)
              }
              onAbort()
              resolve(false)
            },
            () => {
              onAbort()
              resolve(false)
            }
          )
        } catch (error) {
          this.showError(error)
          onAbort()
          resolve(false)
        }
      })
    },
    async saveLocalCourse(previousCourse, { onSuccess = () => {} } = {}) {
      const previousCourseSnapshot = previousCourse || cloneDeep(this.getCurrentCourse)
      if (!this.validateCustomContentBeforeSave()) return false

      try {
        this.isSavingCourse = true
        this.$vs.loading()
        this.saveCourseToStore(this.localCourse)

        const response = await this.saveCourseToApi({
          previousCourse: previousCourseSnapshot,
        })

        this.hydrateLocalCourse(this.getCurrentCourse)
        this.setPersistedCustomContentAudienceType(this.getCurrentCourse)
        this.setPersistedCourseSnapshot(this.localCourse)
        this.getRelatedLPs()

        this.rememberToSave = false
        this.rememberToSaveFiles = false
        this.enterpriseAccountsValidationError = ''

        if (this.$route.name === 'create-course') {
          this.$router.push({
            name: 'edit-course',
            params: { id: response.id },
          })
        }

        this.$vs.notify({
          title: 'Success',
          text: 'Course saved',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'success',
        })
        onSuccess()
        return true
      } catch (error) {
        if (error instanceof CourseSaveFlowError) {
          if (error.context && error.context.course) {
            this.hydrateLocalCourse(this.getCurrentCourse)
            this.setPersistedCustomContentAudienceType(this.getCurrentCourse)
            this.setPersistedCourseSnapshot(this.localCourse)
            if (this.$route.name === 'create-course' && error.context.course.id) {
              this.$router.push({
                name: 'edit-course',
                params: { id: error.context.course.id },
              })
            }
          }

          const customNotification =
            COURSE_SAVE_FLOW_ERROR_NOTIFICATIONS[error.code]
          if (customNotification) {
            this.notifyCustomContentError(customNotification)
            return false
          }
        }
        this.showError(error)
        return false
      } finally {
        this.isSavingCourse = false
        this.$vs.loading.close()
      }
    },
    getDeepArrayIds(item, type) {
      try {
        let input = JSON.parse(JSON.stringify(item))
        let res = input.map(function f(o) {
          return o.content_type === type
            ? o.uuid
            : o.content && o.content.length
            ? o.content.map(f)
            : []
        })
        res = [...new Set(flatten(res, true))]
        return res
      } catch (error) {
        this.showError(error)
      }
    },
    async getAllCourseItems() {
      if (this.localCourse.content && this.localCourse.content.length) {
        const allStructureVideos = this.getDeepArrayIds(
          this.localCourse.content,
          'video'
        )
        const allStructureQuizzes = this.getDeepArrayIds(
          this.localCourse.content,
          'quiz'
        )
        const allStructureExercises = this.getDeepArrayIds(
          this.localCourse.content,
          'exercise'
        )
        const allStructureLabs = this.getDeepArrayIds(
          this.localCourse.content,
          'lab'
        )
        const allStructureIFrames = this.getDeepArrayIds(
          this.localCourse.content,
          'iframe'
        )

        if (allStructureQuizzes.length) {
          await this.getAllQuizzes({
            ids: allStructureQuizzes,
            page_size: allStructureQuizzes.length.toString(),
          })
        }
        if (allStructureVideos.length) {
          await this.getAllVideos({
            ids: allStructureVideos,
            page_size: allStructureVideos.length.toString(),
          })
        }
        if (allStructureExercises.length) {
          await this.getAllExercises({
            ids: allStructureExercises,
            page_size: allStructureExercises.length.toString(),
          })
        }
        if (allStructureLabs.length) {
          await this.getAllLabs({
            ids: allStructureLabs,
            page_size: allStructureLabs.length.toString(),
          })
        }
        if (allStructureIFrames.length) {
          await this.getAllIFrames({
            ids: allStructureIFrames,
            page_size: allStructureIFrames.length.toString(),
          })
        }
      }
    },
    async openToEdit(item) {
      try {
        switch (item.content_type) {
          case 'video':
            this.saveVideoToEdit(this.getVideoEntries[item.uuid])
            break
          case 'quiz':
            this.$vs.loading()
            await this.getQuizAction({ quizId: item.uuid })
            this.$vs.loading.close()
            break
          case 'lab':
            this.$vs.loading()
            await this.getLabAction({ labId: item.uuid })
            await this.fetchObjectives(item.uuid)
            this.$vs.loading.close()
            break
          case 'iframe':
            this.saveIFrameToEdit(this.getIFrameEntries[item.uuid])
            break
          case 'exercise':
            this.saveExerciseToEdit(this.getExerciseEntries[item.uuid])
            break
        }
      } catch (error) {
        this.showError(error)
        this.$vs.loading.close()
      }

      this.selectedId = item.uuid
      this.selectedType = item.content_type
      this.sidebarActive = true
    },
    closeSidebar(event) {
      if (!event && this.preventToCloseSidebar && !this.confirmLeave()) return
      this.sidebarActive = event
      this.selectedType = null

      this.preventToCloseSidebar = false
    },
    closeReviewsSidebar(event) {
      this.sidebarReviews = event
    },
    // @params: {oldName, newName, type}
    renameGroup(e) {
      this.renameGroupOfContentStructure(e)
      this.rememberToSave = true
    },
    // @params: {parent, index, type}
    addGroup(e) {
      this.addGroupToContentStructure(e)
      this.rememberToSave = true
    },
    // @params: {parent, index}
    deleteGroup(e) {
      this.removeGroupToContentStructure(e)
      this.rememberToSave = true
    },
    addLeaf(e) {
      switch (e.type) {
        case 'video':
          this.resetCurrentVideo()
          break
        case 'quiz':
          this.resetCurrentQuiz()
          break
        case 'lab':
          this.resetCurrentLab()
          break
        case 'iframe':
          this.resetCurrentIFrame()
          break
        case 'exercise':
          this.resetCurrentExercise()
          break
      }
      this.selectedParent = e.parent
      this.selectedId = ''
      this.selectedType = e.type
      this.sidebarActive = true
    },
    addLeafToStructure(event) {
      const { type, item } = event
      // add getCurrentVideo to the selected parent
      this.addLeafToContentStructure({
        parent: this.selectedParent,
        leaf: {
          uuid: item.id,
          content_type: type.toLowerCase(),
        },
      })
      this.rememberToSave = true

      this.selectedId = item.id
      this.sidebarActive = false

      if (type.toLowerCase() === 'video') {
        this.addRelatedContent({
          ids: [item.id],
          selectedParent: this.selectedParent,
        })
      }
    },
    deleteLeaf(e) {
      this.removeLeafToContentStructure(e)
      this.rememberToSave = true
    },
    deleteCourseStructure() {
      this.deleteContentStructure()
      this.hydrateLocalCourse(this.getCurrentCourse)
      this.getRelatedLPs()
      this.rememberToSave = true
    },
    async getCourseToEdit(courseId = '') {
      this.$vs.loading()
      await this.getCourseAction({ courseId: courseId })
      this.hydrateLocalCourse(this.getCurrentCourse)
      this.setPersistedCustomContentAudienceType(this.getCurrentCourse)
      this.setPersistedCourseSnapshot(this.localCourse)
      if (
        this.customContentCoursesEnabled &&
        this.localCourse.audience_type === CUSTOM_CONTENT_AUDIENCE_TYPES.CUSTOM
      ) {
        await this.ensureSelectedEnterpriseAccountsLoaded(
          this.localCourse.business_account_ids
        )
      }
      this.getRelatedLPs()
      if (this.localCourse.company.id !== this.getSelectedCompany) {
        // ToDo: Possible bug, we could still be in create
        this.$router.push({ name: 'create-course' })
        this.$vs.notify({
          title: 'Warning',
          text: 'Course you were editing belongs to a different company',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'warning',
        })
      }
      this.getAllCourseItems()
      this.$vs.loading.close()
    },
    async addRelatedContent(params) {
      this.searchingRelatedOf.loading = true
      this.searchingRelatedOf.group = params.selectedParent
      const response = await this.searchRelatedContent(params)
      this.searchingRelatedOf.loading = false
      Object.entries(response).map(([, items]) => {
        items.map((item) => {
          this.addLeafToContentStructure({
            parent: params.selectedParent,
            leaf: {
              uuid: item.uuid,
              content_type: item.content_type.toLowerCase(),
              related: true,
            },
          })
        })
      })
      await this.getAllCourseItems()
    },
    async getRelatedLPs() {
      const listLPaths = this.localCourse.related_learning_paths || []
      if (listLPaths.length) {
        const response = await this.searchLPaths({
          ids: listLPaths,
        })
        this.LPData = {
          count: response.count,
          results: response.results,
        }
      }
    },
    deleteFileFromCourse(file) {
      const index = this.localCourse.files_uuids.indexOf(file.id)
      if (index > -1) {
        this.localCourse.files_uuids.splice(index, 1)
      }
      this.rememberToSaveFiles = true
    },
    addFileToCourse(file) {
      // this.addFile(file)
      this.localCourse.files_uuids = [...this.localCourse.files_uuids, file.id]
      this.rememberToSaveFiles = true
    },
    fileChanged(payload) {
      this.changeFileInCourse(payload)
    },
    preventToClose(event) {
      this.preventToCloseSidebar = event
    },
    onLocalSearchBootcamps(search, loading) {
      this.onSearchBootcamps(search, loading, 'bootcampOptions')
    },
    async getBootcampEntries() {
      if (this.localCourse.bootcamp_origin && !this.bootcampOptions.length) {
        // we need to get the bootcamps to show their names
        const ids = []
        if (this.localCourse.bootcamp_origin)
          ids.push(this.localCourse.bootcamp_origin)
        const response = await this.searchBootcamps({
          ids: ids,
        })
        this.bootcampOptions = response.results.reduce((acc, v) => {
          acc.push({ label: v.name, value: v.id })
          return acc
        }, [])
      }
    },
    validateMainLArea() {
      return !(
        this.localCourse.learning_areas &&
        this.localCourse.learning_areas.includes(
          this.localCourse.main_learning_area
        )
      )
    },
  },
  async mounted() {
    try {
      this.$vs.loading({
        container: '#course-async-api-data-2',
        scale: 0.6,
      })

      // @mixin
      await this.getBasicData()
    } catch (error) {
      this.$vs.loading.close('#course-async-api-data-2 > .con-vs-loading')
      this.showError(error)
    }
    this.$vs.loading.close('#course-async-api-data-2 > .con-vs-loading')
  },
  async created() {
    this.searchEnterpriseAccountsDebounced = debounce(async (
      search,
      loading,
      requestId
    ) => {
      const updateLoading =
        typeof loading === 'function' ? loading : () => {}
      try {
        await this.loadEnterpriseAccountsUntilLocalMatchOrExhaustion(
          search,
          requestId
        )
      } catch (error) {
        this.notifyEnterpriseAccountsLoadError()
      } finally {
        if (requestId === this.enterpriseAccountsSearchRequestId) {
          this.syncEnterpriseAccountsNoResults(search)
          updateLoading(false)
        }
      }
    }, 350)

    if (this.$route && this.$route.params.id) {
      if (this.$route.params.id !== this.localCourse.id) {
        try {
          await this.getCourseToEdit(this.$route.params.id)
          this.getBootcampEntries()
        } catch (error) {
          this.showError(error)
          this.$router.push({ name: 'create-course' })
        }
        this.$vs.loading.close()
      }
    } else {
      this.resetCurrentCourse()
      this.hydrateLocalCourse(this.getCurrentCourse)
      this.setPersistedCustomContentAudienceType(this.getCurrentCourse)
      this.setPersistedCourseSnapshot(this.localCourse)
      this.getRelatedLPs()
    }
  },
  watch: {
    customContentCoursesEnabled: function (newValue) {
      if (newValue) {
        // If the flag turns ON at runtime while editing a custom course,
        // proactively hydrate selected account labels (non-blocking).
        if (this.isCustomContentEnabled) {
          this.ensureSelectedEnterpriseAccountsLoaded(
            this.localCourse.business_account_ids
          ).catch(() => {
            this.notifyEnterpriseAccountsLoadError()
          })
        }
        return
      }

      this.resetEnterpriseAccountsFeatureState()
      this.localCourse.audience_type = CUSTOM_CONTENT_AUDIENCE_TYPES.PUBLIC
      this.localCourse.business_account_ids = []
    },
    'localCourse.name': function (newValue) {
      if (this.autoAlias) {
        this.localCourse.slug = slug(newValue)
      }
    },
    'getCurrentCourse.content': function () {
      this.localCourse.content = this.getCurrentCourse.content
    },
    autoAlias: function (newValue) {
      if (newValue) {
        this.localCourse.slug = slug(this.localCourse.name)
      }
    },
    'localCourse.files_uuids': async function () {
      if (
        JSON.stringify(this.localCourse.files_uuids) ===
        JSON.stringify(this.getCurrentCourse.files_uuids)
      ) {
        this.rememberToSaveFiles = false
      }
      if (this.localCourse.files_uuids && this.localCourse.files_uuids.length) {
        this.filesLoading = true
        await this.getAllCourseFiles(this.localCourse.files_uuids)
        this.filesLoading = false
      }
      if (
        this.localCourse.files_uuids &&
        !this.localCourse.files_uuids.length
      ) {
        this.resetFilesInCourse()
      }
    },
    'localCourse.business_account_ids': function (newValue) {
      const normalizedIds = normalizeBusinessAccountIds(newValue)
      if (!isEqual(newValue, normalizedIds)) {
        this.localCourse.business_account_ids = normalizedIds
        return
      }

      if (this.customContentCoursesEnabled && this.isCustomContentEnabled) {
        this.$nextTick(() => {
          this.attachEnterpriseAccountsScrollListener()
        })
      }

      if (normalizedIds.length) {
        this.enterpriseAccountsValidationError = ''
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    this.handleUnsavedChangesBeforeRouteLeave(to, next)
  },
  beforeDestroy() {
    this.cancelSearchEnterpriseAccountsDebounced()
    this.detachEnterpriseAccountsScrollListener()
  },
}
</script>
<style lang="scss">
.course-form {
  .enterprise-accounts-dd-enter-active,
  .enterprise-accounts-dd-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
    transform-origin: top center;
  }

  .enterprise-accounts-dd-enter,
  .enterprise-accounts-dd-leave-to {
    opacity: 0;
    transform: translateY(-6px);
  }

  .con-img-upload {
    padding: 0;
  }
  .content-left-panel {
    .con-img-upload {
      .img-upload {
        height: 100px;
        width: 100px;
      }
      .text-archive {
        font-size: 0.8em;
      }
    }
  }
  .content-right-panel {
    .con-img-upload {
      display: flex;
      .img-upload {
        height: 80px;
        margin: 0;
      }
    }
  }
  .con-input-upload {
    margin: 0;
    width: 100%;
    height: 80px;
  }
  .content-right-panel {
    .con-input-upload {
      height: 80px;
    }
  }
  .import-csv-errors {
    list-style-type: disc;
  }
  .media-files {
    .prepend-text {
      padding: 0;
      .course-image {
        width: 53px;
      }
    }
  }
}
.vs-sidebar-position-right {
  max-width: 60%;
  padding: 30px;
  .vs-sidebar--items {
    overflow: auto;
  }
}

.vs-con-loading__container {
  position: relative;
  overflow: unset;
}

</style>
